import classNames from 'classnames';
import { ComponentProps, type JSX, MouseEvent, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import useIsMobileSizedScreen from '../../hooks/useIsMobileSizedScreen';
import CheckboxGroup from '../CheckboxGroup';
import PopupMenu from '../PopupMenu';
import { ButtonDefinition } from '../PopupMenu/PopupMenu';
import RadioGroup from '../RadioGroup';
import { RangeSlider, Slider } from '../Slider';
import styles from './styles/styles.module.scss';
import { FilterConfig } from './types';

type FilterDropdownProps = Pick<ComponentProps<typeof PopupMenu>, 'anchor' | 'isOpen' | 'onCloseRequest'> & {
  popupMenuContainerClassName?: string;
  config: FilterConfig;
};

const FilterDropdown = ({ popupMenuContainerClassName, config, ...popupProps }: FilterDropdownProps): JSX.Element => {
  const isMobile = useIsMobileSizedScreen();
  const content = useMemo(() => {
    switch (config.type) {
      case 'checkbox': {
        const checkboxGroup = (
          <CheckboxGroup
            {...config.checkboxGroup}
            className={styles.checkboxGroup}
            checkboxClassName={styles.checkbox}
            labelText={config.showLabel ? config.checkboxGroup.labelText : undefined}
            size={isMobile ? 'md' : 'sm'}
          />
        );
        return config.additionalContent ? (
          <>
            {checkboxGroup}
            <div className={styles.additionalContentContainer}>{config.additionalContent}</div>
          </>
        ) : (
          checkboxGroup
        );
      }
      case 'radio': {
        const radioGroup = (
          <RadioGroup
            {...config.radioGroup}
            className={styles.radioGroup}
            labelText={config.showLabel ? config.radioGroup.labelText : undefined}
            radioClassName={styles.radio}
            size={isMobile ? 'md' : 'sm'}
          />
        );
        return config.additionalContent ? (
          <>
            {radioGroup}
            <div className={styles.additionalContentContainer}>{config.additionalContent}</div>
          </>
        ) : (
          radioGroup
        );
      }
      case 'range-slider': {
        return (
          <RangeSlider
            {...config.rangeSlider}
            className={styles.rangeSlider}
            headerValueTextVariant="text100"
            label={config.showLabel ? config.rangeSlider.label : undefined}
          />
        );
      }
      case 'slider': {
        return (
          <Slider
            {...config.slider}
            className={styles.slider}
            headerValueTextVariant="text100"
            label={config.showLabel ? config.slider.label : undefined}
          />
        );
      }
      case 'custom-dropdown': {
        return config.content;
      }
      default: {
        return null;
      }
    }
  }, [config, isMobile]);

  const buttons = useMemo(() => {
    const buttonDefs: ButtonDefinition[] = [];

    const canClear = Boolean(config.filterChip.onClear);
    const clearButton: ButtonDefinition = {
      disabled: !canClear,
      onClick: e => {
        config.filterChip.onClear?.(e as MouseEvent<HTMLButtonElement>);
        popupProps.onCloseRequest();
      },
      testId: 'filter-clear',
      text: <FormattedMessage defaultMessage="Clear" />,
      variant: isMobile ? 'default' : 'flat'
    };
    if (isMobile || canClear) {
      buttonDefs.push(clearButton);
    }

    const canApply = Boolean(config.onApplyClick);
    const applyButton: ButtonDefinition = {
      onClick: () => {
        config.onApplyClick?.();
        popupProps.onCloseRequest();
      },
      testId: 'filter-apply',
      text: <FormattedMessage defaultMessage="Apply" />,
      variant: isMobile ? 'primary' : 'flat'
    };
    if (isMobile || canApply) {
      buttonDefs.push(applyButton);
    }

    return buttonDefs;
  }, [config, isMobile, popupProps]);

  return (
    <PopupMenu
      {...popupProps}
      buttons={buttons}
      containerClassName={classNames(styles.popupContainer, popupMenuContainerClassName)}
      headerAndContentClassName={styles.popupContents}
      hideCloseButton={!isMobile}
      purchaseTier={config.purchaseTier}
      title={isMobile ? config.filterChip.unselectedText : undefined}
      useBottomSheet={isMobile}
    >
      {content}
    </PopupMenu>
  );
};

export default FilterDropdown;
